import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { COLOR } from 'constants/colors';
import { useMediaQuery } from 'react-responsive';
import useFlashToggler from 'hooks/use-flash-toggler';
import { get, getDefaultHeaders, post } from '__utils/ajax-wrapper';
import { useDispatch, useSelector } from 'react-redux';
import CartSummary from 'components/cart-summary/index';
import { CommonStyledComponentProps } from '__utils/type';
import { LayoutContext } from 'components/app-routes';
import BankDetails from './bank-details';
import VerifyMandate from './verify-mandate';
import { ENDPOINTS } from 'constants/endpoints';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import resolveURLParams from '__utils/url-params-resolver';
import { storeMandateActivateConfig } from '../mandate.actions';
import { hideSharePopupAction } from 'components/share/share.actions';
import OnboardingProgressSteps from 'components/onboarding-progress-steps';
import { ProfileProgressStepsList } from 'constants/progress-steps-constants';
import { WhiteCardWrapper, CommonTitleDiv, BoldText, MandateHr } from 'views/common-styled-components';
import { getName, isFlowTypeCheckout, getURLwithoutPro, isProUser, getQueryParamsFromString, getEncryptedUserId, redirectToHome, redirectToOld } from '__utils/common-utils';
import '../../../font-icon.css';
import { propsToJS } from '__utils/immutable-to-js';
import UserSelector from 'views/user/user.selector';
import { fetchUserProfileInfo } from 'views/user/user.actions';
import { useDeepCompare } from 'hooks/use-deep-memo';
import { EmptyBankDetails } from '../empty-invalid-mandates';
import { getUserDetails } from '__utils/storage';
import elementVisibilty from '__utils/toggleVisibilty-util';
import { SuccessBtnWhiteGreenBorder } from 'components/button';
import CustomTag from 'components/tag';
import CartSelector from 'views/cart/cart.selector';
import ShareButton from 'components/share-button';


const MandateDetailsCard = styled.div<CommonStyledComponentProps>`
    .inner-cont {
        padding: 0 0 15px 0px;
        display: flex;
    }
    .shareDiv{
        width: 200px;
        margin: 12px 0 16px 0;
    }
`;

const MandateWrapper = styled(WhiteCardWrapper) <any>`
    box-shadow: ${props => (props.isFlowTypeCheckout ? `0px 2px 8px ${COLOR.LIGHTGREY}` : 'none')};
`;
const SkipNowBtn = styled(SuccessBtnWhiteGreenBorder)`
    padding: 5px;
    height: unset;
`;

const LargeBoldTXT = styled(BoldText)`
     font-size: 18px;
`;

const SetupMandate: React.FC = () => {

    const dispatch = useDispatch();
    const flash = useFlashToggler();
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const { userType, ucc, mandateId, mandateLimit } = getQueryParamsFromString(window.location.search);
    const { userProfileInfo } = propsToJS(useSelector(UserSelector));
    const { cartDetails } = propsToJS(useSelector(CartSelector));
    const [mandateActivateConfig, setMandateActivateConfig] = useState<any>();
    const [selectedMandateInput, setSelectedMandateInput] = useState<string>('');
    const [selectedPaymentTypeInput, setSelectedPaymentTypeInput] = useState<string>('');
    const [bankDetailsAbsent, setBankDetailsAbsent] = useState<boolean>(false);
    const [sipsAvailableInCart, setSipsAvailableInCart] = useState<boolean>(false);
    const isCartFull = cartDetails && cartDetails?.noOfItems > 0;

    // eslint-disable-next-line
    const { layoutProps, SetLayoutProps } = React.useContext(LayoutContext);

    useEffect(() => {
        SetLayoutProps({
            config: { title: isFlowTypeCheckout() ? 'Investor Profile' : 'Setup New Mandate' }
        });

        if (getUserDetails()) {
            if (ucc) {
                getEncryptedUserId() === ucc ? dispatch(fetchUserProfileInfo()) :
                    flash.error('Oops! You are not authorised to view this page', 6000, () => redirectToHome())
            }
        }
        else elementVisibilty.show('loginDiv');

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userProfileInfo) {
            (userProfileInfo?.userBankDetails?.bankName ? activateMandateConfig() : setBankDetailsAbsent(true));
        }
    }, [useDeepCompare(userProfileInfo)]);

    const getSipsInCart = (sipsInCart: boolean) => {
        setSipsAvailableInCart(sipsInCart)
    }

    //@purpose: to get initial mandate details of user 
    const activateMandateConfig = () => {
        get(ENDPOINTS.GET_ACTIVATE_MANDATE_CONFIG, getDefaultHeaders(), true)
            .subscribe((response: AjaxResponse | AjaxError) => {
                if (response.response?.operationMessage == "success" && response.response?.responseData) {
                    const result = response.response?.responseData;
                    dispatch(storeMandateActivateConfig(result));
                    setMandateActivateConfig(result);
                    if (mandateId || getUserDetails()?.taxStatus !== '01') {
                        setSelectedMandateInput('X')
                    } else if (result.emandateAllowed && !mandateId) {
                        setSelectedMandateInput('N')
                    }
                    if (result.netBankingAllowed) {
                        setSelectedPaymentTypeInput('Net')
                    } else {
                        setSelectedPaymentTypeInput('Debit')
                    }
                }
            }, (error: AjaxError) => {
                !error.status && flash.error('Something went wrong.');
            });
    }

    const getShareMandateMsg = () => {
        return window.encodeURIComponent(
            `Dear ${getName()},\n\nYour digitial partner has requested you to setup a mandate with us to avoid missing on your SIP payments. Once setup, you won’t be required to transfer money every month as it will be auto-debited from your account.\n\nFor a faster approval, we recommend you to setup online, by verifying your account via your debit card or netbanking.\n\nIn case your bank does not support e-NACH mandates, you also have an option to setup through a physical form.\n\nClick the following link to proceed: \n${getShareURL()}`);
    }

    const getShareURL = () => {
        return (mandateId && mandateLimit) ?
            getURLwithoutPro(`/v1/profile/mandates/setup-mandate?flowType=checkout&userType=old&ucc=${getEncryptedUserId()}&mandateId=${mandateId}&mandateLimit=${mandateLimit}`) :
            getURLwithoutPro(`/v1/profile/mandates/setup-mandate?flowType=checkout&userType=old&ucc=${getEncryptedUserId()}`)
    }

    //@purpose: share via email/sms
    const shareMandateDetails = (shareTo: string, type: string) => {
        const shareType = type === 'email' ? 'email' : 'sms';
        post(resolveURLParams(`${ENDPOINTS.POST_SHARE_MANDATES_LINK}?type=${shareType}&value=${shareTo}&mandateSetupLink=${window.encodeURIComponent(getShareURL().replace(/&amp;/g, "&"))}`, null, {}), {}, getDefaultHeaders(), true)
            .subscribe((response: AjaxResponse | AjaxError) => {
                if (response?.response?.meta?.message === 'success') {
                    flash.success('Mandate link shared successfully!!!');
                    dispatch(hideSharePopupAction());
                }

            }, (error: AjaxError) => {
                flash.error("Failed to share mandate link. Please try again later.");
                dispatch(hideSharePopupAction());
            });
    }


    return (<div>
        <MandateWrapper isMobile={isMobile} isFlowTypeCheckout={isFlowTypeCheckout()} fullWidth>
            <MandateDetailsCard isMobile={isMobile}>
                {(!isMobile && (isFlowTypeCheckout() && !userType)) && <CartSummary />}
                {!isMobile && (isFlowTypeCheckout() && !userType) && <CommonTitleDiv>Investor Profile</CommonTitleDiv>}
                {isFlowTypeCheckout() && (!userType) ?
                    <OnboardingProgressSteps
                        stepsList={ProfileProgressStepsList}
                        currentStep={4}
                        stepTag={
                            sipsAvailableInCart ? <CustomTag variant={'success'} fontSize='10'>MANDATORY</CustomTag> :
                                <SkipNowBtn onClick={() => isCartFull ? redirectToOld('cart-review') : redirectToHome()}> Go to Homepage</SkipNowBtn>
                        }
                    />
                    :
                    <div className='mt24'>
                        {isMobile ? <BoldText>Bank Mandate</BoldText> : <LargeBoldTXT>Setup Bank Mandate</LargeBoldTXT>}
                    </div>
                }

                <div className="inner-cont">
                    <div className={isMobile ? 'w100' : 'w60'}>
                        <p>Never miss your SIP payments by setting up auto pay mandate. </p>
                        <div>
                            {isProUser() && mandateActivateConfig && <div className='shareDiv'>
                                <ShareButton shareConfig=
                                    {{
                                        message: getShareMandateMsg(),
                                        shareUrl: getShareURL(),
                                        onEmailOrSMSShare: shareMandateDetails,
                                        shareType: 'shareMandateDetails',
                                        mixpanelData: { eventName: 'mandateShare', source: 'mandates' },
                                        sharePopupText: 'Share with customer',
                                        shareTitle: <p className='bold ft16'>Share with customer</p>
                                    }} />
                            </div>}
                            <MandateHr style={{ marginTop: '16px' }} />
                        </div>
                        {mandateActivateConfig && <BankDetails bankInfo={userProfileInfo?.userBankDetails} />}
                    </div>
                </div>
                {mandateActivateConfig && <VerifyMandate getSipsInCart={getSipsInCart} selectedMandateInput={selectedMandateInput} selectedPaymentTypeInput={selectedPaymentTypeInput} emandateDisabled={false} />}
            </MandateDetailsCard>
            {(bankDetailsAbsent && !mandateActivateConfig) && <EmptyBankDetails />}
        </MandateWrapper>
    </div>);
};

export default SetupMandate;

